import { useEffect, useState } from 'react';
import { Button } from '../../../modules/design-system';
import {
  getPoolShipping,
  type ApiClient,
  type IframePoolShippingDetail,
} from '../../../modules/api';
import { DeliveryCard, DeliveryPageShell } from '../shared';
import { SuccessSection } from './sections/SuccessSection';
import { AddressSection } from './sections/AddressSection';
import { ServiceSection } from './sections/ServiceSection';
import { ItemsSection } from './sections/ItemsSection';

export interface DeliveryShowPageProps {
  client: ApiClient;
  shippingId: number;
  /** Top-bar "< Home" → return to My Packs (Deliveries tab). */
  onBack: () => void;
  /** Drop the noise-SVG overlay from the page gradient. */
  hideBackgroundPattern?: boolean;
}

/**
 * Delivery Show page (Figma 361:132067) — read-only view of an existing
 * pool_shipping. Two stacked glass cards:
 *   1. Success / Support — confirmation copy + "Ask a Question" CTA.
 *   2. Details — address, chosen service + paid positions, item grid.
 * A plain-text "Cancel Delivery" link sits below the cards while the
 * shipping is still cancellable.
 *
 * Data comes from the mocked `getPoolShipping(id)` — see TODO in the
 * api module.
 */
export function DeliveryShowPage({
  client,
  shippingId,
  onBack,
  hideBackgroundPattern = false,
}: DeliveryShowPageProps) {
  const [shipping, setShipping] = useState<IframePoolShippingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getPoolShipping(client, shippingId)
      .then((res) => {
        if (cancelled) return;
        setShipping(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load delivery');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, shippingId]);

  if (loading) {
    return (
      <DeliveryPageShell
        title="Delivery Information"
        onBack={onBack}
        hideBackgroundPattern={hideBackgroundPattern}
      >
        <DeliveryCard>
          <p className="text-[14px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Loading delivery…
          </p>
        </DeliveryCard>
      </DeliveryPageShell>
    );
  }

  if (error || !shipping) {
    return (
      <DeliveryPageShell
        title="Delivery Information"
        onBack={onBack}
        hideBackgroundPattern={hideBackgroundPattern}
      >
        <DeliveryCard>
          <p role="alert" className="text-[14px]" style={{ color: '#F87171' }}>
            {error ?? 'Delivery not found.'}
          </p>
        </DeliveryCard>
      </DeliveryPageShell>
    );
  }

  return (
    <DeliveryPageShell title="Delivery Information" onBack={onBack}>
      <div className="flex flex-col gap-4">
        <DeliveryCard>
          <SuccessSection
            onAskQuestion={() => {
              // TODO(vitaly): wire to the real support channel once the
              // iframe ships a contact flow.
            }}
          />
        </DeliveryCard>

        <DeliveryCard>
          <div className="flex flex-col gap-6">
            <AddressSection address={shipping.address} />
            <ServiceSection
              service={shipping.service}
              positions={shipping.positions}
            />
            <ItemsSection items={shipping.items} />
          </div>
        </DeliveryCard>

        {shipping.isCanCancel && (
          <div className="mt-2">
            <Button variant="ghost" size="md" fullWidth onClick={onBack}>
              Cancel Delivery
            </Button>
          </div>
        )}
      </div>
    </DeliveryPageShell>
  );
}
